import { powerLawBand, daysSinceGenesis, GENESIS } from './powerLaw.js';

// DCA simulator math — pure functions, no React, easy to unit-test.
//
// Approach
// --------
// We don't project BTC's *price* with a point estimate. We bucket each
// future buy against three power-law curves (floor, fair, ceiling), so
// the user sees a band rather than a single number that can be quoted
// out of context as a "prediction".
//
// For each contribution period from `startDate` to `endDate` (stepped
// by `frequency`), we compute the band at that period's date and
// divide the contribution amount by each curve's price. The cumulative
// BTC accumulated under each curve is the band's bound at that horizon.
//
// Inputs
// ------
//   amountPerPeriod  — USD invested per period
//   frequency        — 'weekly' | 'biweekly' | 'monthly'
//   startDate        — Date object (when the user starts their DCA)
//   horizonYears     — projection horizon in years (1, 5, 10, etc.)
//
// Output
// ------
//   {
//     totalContributed: USD,
//     periods: number of buys,
//     band: {
//       floor:   { btc, usdAtFloor }    // most BTC accumulated, lowest USD value
//       fair:    { btc, usdAtFair }
//       ceiling: { btc, usdAtCeiling }  // least BTC, highest USD
//     }
//   }
//
// Note on intuition: when price is *low* (floor curve), each $X buys
// MORE BTC, so floor produces the largest BTC accumulation. Ceiling
// produces the fewest. We then value all three BTC totals at the SAME
// horizon-fair-value price so the USD spread directly reflects the
// stacking-luck range — "if the next decade trends low you accumulate
// more BTC, here's what that's worth at fair value at horizon".
// (Valuing each curve at its own matching horizon curve cancels the
// band entirely — same exp, different coefs → identical USD totals,
// which is mathematically true but useless as a UX signal.)

const FREQUENCY_DAYS = {
  weekly:   7,
  biweekly: 14,
  monthly:  30.44, // average month length — close enough for projections
};

export function simulateDca({
  amountPerPeriod,
  frequency = 'monthly',
  startDate,
  horizonYears,
}) {
  if (!Number.isFinite(amountPerPeriod) || amountPerPeriod <= 0) return null;
  if (!Number.isFinite(horizonYears) || horizonYears <= 0) return null;
  const stepDays = FREQUENCY_DAYS[frequency];
  if (!stepDays) return null;
  if (!(startDate instanceof Date) || Number.isNaN(startDate.getTime())) return null;

  const startMs = startDate.getTime();
  const endMs = startMs + horizonYears * 365.25 * 86_400_000;

  // Reject pathological inputs that would burn a tab. ~1900 weeks max
  // covers 36 years of weekly DCA — more than enough.
  const stepMs = stepDays * 86_400_000;
  const periodCount = Math.floor((endMs - startMs) / stepMs) + 1;
  if (periodCount > 2000) return null;

  let btcFloor = 0, btcFair = 0, btcCeiling = 0;
  let totalContributed = 0;

  for (let i = 0; i < periodCount; i++) {
    const tMs = startMs + i * stepMs;
    const t = new Date(tMs);
    // Power-law model is undefined before genesis — clamp to genesis+1
    // for any backdated start date so the math doesn't blow up.
    const evalDate = tMs < GENESIS.getTime() + 86_400_000
      ? new Date(GENESIS.getTime() + 86_400_000)
      : t;
    const band = powerLawBand(evalDate);
    if (!Number.isFinite(band.floor) || band.floor <= 0) continue;

    btcFloor   += amountPerPeriod / band.floor;
    btcFair    += amountPerPeriod / band.fair;
    btcCeiling += amountPerPeriod / band.ceiling;
    totalContributed += amountPerPeriod;
  }

  // Value all three BTC totals at the SAME horizon-fair price so the
  // USD spread reflects the stacking-luck range rather than collapsing
  // (same exp on every curve → matching-curve USD totals are identical).
  const horizonBand = powerLawBand(new Date(endMs));
  const horizonFair = horizonBand.fair;

  return {
    totalContributed,
    periods: periodCount,
    horizonDate: new Date(endMs),
    horizonFairPrice: horizonFair,
    band: {
      floor:   { btc: btcFloor,   usd: btcFloor   * horizonFair },
      fair:    { btc: btcFair,    usd: btcFair    * horizonFair },
      ceiling: { btc: btcCeiling, usd: btcCeiling * horizonFair },
    },
  };
}

// Helper for the UI — return three projections (1Y/5Y/10Y) at once.
export function simulateMultiHorizon(params, horizonsYears = [1, 5, 10]) {
  return horizonsYears.map(y => ({
    horizonYears: y,
    result: simulateDca({ ...params, horizonYears: y }),
  }));
}
